import { MapPin, History, PencilLine } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { gql } from '@apollo/client';
import { useFragment, useSuspenseQuery } from '@apollo/client/react';

import { useSaasEscrow } from 'src/context/saas-escrow';
import {
  EscrowPartyTypeEnum,
  DisruptionNotificationEnum,
  AgreementFormEnum,
  SuggestedChangeChangeTypeEnum,
} from 'src/graphql/schema';
import {
  ISuggestedChangesQuery,
  SuggestedChangesDocument,
} from 'src/graphql/actions/suggestChanges/suggestedChanges.query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { readOnlyStorageRegionDictionary } from 'src/shared/entities/storage-regions';
import { cn } from 'src/lib/utils';
import { Button } from '@/components/ui/button';
import { SuggestedChangesView } from 'src/components/SuggestChanges';
import { getTechnicalSuggestedChangesItems, RequestChangesTooltip } from 'src/containers/SaasEscrow/RequestChanges';
import { Badge } from '@/components/ui/badge';
import {
  gracePeriodOptionsDictionary,
  disruptedNotificationsOptionsRichDictionary,
} from 'src/shared/entities/escrow-notification-options';
import { getEscrowActivityNotificationOptions } from 'src/shared/entities/escrow-activity-notification-options';
import {
  activationNotificationOptionsRichDictionary,
  assetVisibilityOptionsRichDictionary,
} from 'src/containers/SoftwareEscrow/Technical';
import { assetVersionsLimitOptionsDictionary } from 'src/shared/entities/asset-versions-limit-options';

const { activityNotificationOptionsRichDictionary } = getEscrowActivityNotificationOptions('software');

type ICardWrapper = {
  title: string;
  description: string;
  secondaryDescription?: string;
};

type ICardInfo = {
  title: string;
  description?: string;
  descriptionStyle?: 'italic' | 'normal';
  icon: React.ElementType;
  badge?: React.ReactNode;
};

const CardInfo = ({ title, description, icon: Icon, descriptionStyle = 'normal', badge }: ICardInfo) => {
  return (
    <div className="flex items-start gap-3 bg-accent shadow-sm rounded-lg p-4">
      <Icon className="size-3.5 text-foreground shrink-0 mt-1" />
      <div className="grow">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {description && (
          <p className={cn('text-foreground/80 text-xs', descriptionStyle === 'italic' ? 'italic' : 'font-normal')}>
            {description}
          </p>
        )}
      </div>
      {badge}
    </div>
  );
};

const CardWrapper = ({ title, description, secondaryDescription, children }: React.PropsWithChildren<ICardWrapper>) => {
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-lg">{title}</h2>
        <p className="text-muted-foreground text-sm">{description}</p>
        {secondaryDescription && (
          <Fragment>
            <br />
            <p className="italic text-muted-foreground text-sm">{secondaryDescription}</p>
          </Fragment>
        )}
      </div>
      {children}
    </div>
  );
};

const SuggestedChangeFragment = gql`
  fragment SuggestedChangeFragment on SuggestChange {
    id
    payload
  }
`;

type ISuggestedChangePayload = {
  beneficiary_shared_information: boolean;
  depositor_activity_notification: string;
  depositor_disruption_notification: string;
  depositor_disruption_notification_grace_period: string;
  beneficiary_activity_notification: string;
  beneficiary_disruption_notification: string;
  beneficiary_disruption_notification_grace_period: string;
  activation_notification: string;
};

const NotificationsSettings = ({
  partyType,
  suggestedChangeId,
}: {
  partyType: EscrowPartyTypeEnum;
  suggestedChangeId?: string;
}) => {
  const {
    saasEscrow: {
      depositor,
      beneficiary,
      depositorActivityNotification,
      depositorDisruptionNotification,
      depositorDisruptionNotificationGracePeriod,
      beneficiaryActivityNotification,
      beneficiaryDisruptionNotification,
      beneficiaryDisruptionNotificationGracePeriod,
    },
  } = useSaasEscrow();
  const { data: suggestedChange } = useFragment<{
    id: string;
    payload: ISuggestedChangePayload;
  }>({
    fragment: SuggestedChangeFragment,
    from: {
      __typename: 'SuggestChange',
      id: suggestedChangeId ?? '',
    },
  });

  const partySuggestedChanges = {
    [EscrowPartyTypeEnum.Depositor]: {
      activityNotification: suggestedChange.payload?.[`depositor_activity_notification`],
      disruptionNotification: suggestedChange.payload?.[`depositor_disruption_notification`],
      disruptionNotificationGracePeriod: suggestedChange.payload?.[`depositor_disruption_notification_grace_period`],
    },
    [EscrowPartyTypeEnum.Beneficiary]: {
      activityNotification: suggestedChange.payload?.['beneficiary_activity_notification'],
      disruptionNotification: suggestedChange.payload?.['beneficiary_disruption_notification'],
      disruptionNotificationGracePeriod: suggestedChange.payload?.['beneficiary_disruption_notification_grace_period'],
    },
  };

  const partyNotificationData = {
    [EscrowPartyTypeEnum.Depositor]: {
      activityNotification: depositorActivityNotification,
      disruptionNotification: depositorDisruptionNotification,
      disruptionNotificationGracePeriod: depositorDisruptionNotificationGracePeriod,
    },
    [EscrowPartyTypeEnum.Beneficiary]: {
      activityNotification: beneficiaryActivityNotification,
      disruptionNotification: beneficiaryDisruptionNotification,
      disruptionNotificationGracePeriod: beneficiaryDisruptionNotificationGracePeriod,
    },
  };

  const partySuggestedChangesData = partySuggestedChanges[partyType];

  if (partyType === EscrowPartyTypeEnum.Depositor && !depositor)
    return (
      <Card className="bg-muted">
        <CardHeader className="pb-6">
          <CardTitle>Activity & disrupted access notifications</CardTitle>
          <CardDescription className="italic">
            The depositor members will be able to configure their own notification settings.
          </CardDescription>
        </CardHeader>
      </Card>
    );

  if (partyType === EscrowPartyTypeEnum.Beneficiary && !beneficiary) return null;

  return (
    <Fragment>
      <Card>
        <CardHeader>
          <CardTitle>Activity notification</CardTitle>
          <CardDescription>
            The notification types set to be delivered to the escrow members. Information about the deposited assets
            include names and deposit dates.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {partySuggestedChangesData.activityNotification && (
            <SuggestedChangesView
              items={[
                getTechnicalSuggestedChangesItems(
                  `${partyType}ActivityNotification`,
                  partySuggestedChangesData.activityNotification,
                ),
              ]}
              className="mb-6"
            />
          )}
          <CardInfo
            title={
              activityNotificationOptionsRichDictionary[partyNotificationData[partyType].activityNotification].label
            }
            description={
              activityNotificationOptionsRichDictionary[partyNotificationData[partyType].activityNotification]
                .description
            }
            icon={activityNotificationOptionsRichDictionary[partyNotificationData[partyType].activityNotification].icon}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Disrupted access notifications</CardTitle>
          <CardDescription>
            The notification type set to be delivered to the escrow members if access to an asset is disrupted, and the
            asset can no longer be deposited.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(partySuggestedChangesData.disruptionNotification ||
            partySuggestedChangesData.disruptionNotificationGracePeriod) && (
            <SuggestedChangesView
              items={[
                ...(partySuggestedChangesData.disruptionNotification
                  ? [
                      getTechnicalSuggestedChangesItems(
                        `${partyType}DisruptionNotification`,
                        partySuggestedChangesData.disruptionNotification,
                      ),
                    ]
                  : []),
                ...(partySuggestedChangesData.disruptionNotificationGracePeriod
                  ? [
                      getTechnicalSuggestedChangesItems(
                        `${partyType}DisruptionNotificationGracePeriod`,
                        partySuggestedChangesData.disruptionNotificationGracePeriod,
                      ),
                    ]
                  : []),
              ]}
              className="mb-6"
            />
          )}
          <CardInfo
            title={getDisruptedNotificationTitle(
              partyNotificationData[partyType].disruptionNotification,
              partyNotificationData[partyType].disruptionNotificationGracePeriod ?? '',
            )}
            icon={
              disruptedNotificationsOptionsRichDictionary[partyNotificationData[partyType].disruptionNotification].icon
            }
          />
        </CardContent>
      </Card>
    </Fragment>
  );
};

const TripartiteNotificationSettings = ({ suggestedChangeId }: { suggestedChangeId: string }) => {
  const {
    saasEscrow: { beneficiarySharedInformation },
  } = useSaasEscrow();
  const { data: suggestedChange } = useFragment<{
    id: string;
    payload: ISuggestedChangePayload;
  }>({
    fragment: SuggestedChangeFragment,
    from: {
      __typename: 'SuggestChange',
      id: suggestedChangeId ?? '',
    },
  });

  return (
    <CardWrapper
      title="Notification settings for the beneficiary"
      description="Information sharing, and the notifications sent to the beneficiary escrow members."
    >
      <Card>
        <CardHeader>
          <CardTitle>Asset visibility</CardTitle>
          <CardDescription>
            The beneficiary’s permission to access deposit information, including names of assets, deposit dates, and
            asset connection status.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {suggestedChange.payload?.['beneficiary_shared_information'] && (
            <SuggestedChangesView
              items={[
                getTechnicalSuggestedChangesItems(
                  'beneficiarySharedInformation',
                  suggestedChange.payload?.['beneficiary_shared_information'],
                ),
                ...(suggestedChange.payload?.['beneficiary_disruption_notification']
                  ? [
                      getTechnicalSuggestedChangesItems(
                        'beneficiaryDisruptionNotification',
                        suggestedChange.payload?.['beneficiary_disruption_notification'],
                      ),
                    ]
                  : []),
                ...(suggestedChange.payload?.['beneficiary_disruption_notification_grace_period']
                  ? [
                      getTechnicalSuggestedChangesItems(
                        'beneficiaryDisruptionNotificationGracePeriod',
                        suggestedChange.payload?.['beneficiary_disruption_notification_grace_period'],
                      ),
                    ]
                  : []),
              ]}
              className="mb-6"
            />
          )}
          <CardInfo
            title={assetVisibilityOptionsRichDictionary[beneficiarySharedInformation.toString()].label}
            icon={assetVisibilityOptionsRichDictionary[beneficiarySharedInformation.toString()].icon}
            description={assetVisibilityOptionsRichDictionary[beneficiarySharedInformation.toString()].description}
          />
        </CardContent>
      </Card>
      {beneficiarySharedInformation && (
        <NotificationsSettings partyType={EscrowPartyTypeEnum.Beneficiary} suggestedChangeId={suggestedChangeId} />
      )}
    </CardWrapper>
  );
};

const BipartiteNotificationSettings = ({ suggestedChangeId }: { suggestedChangeId: string }) => {
  const {
    saasEscrow: { activationNotification },
  } = useSaasEscrow();
  const { data: suggestedChange } = useFragment<{
    id: string;
    payload: ISuggestedChangePayload;
  }>({
    fragment: SuggestedChangeFragment,
    from: {
      __typename: 'SuggestChange',
      id: suggestedChangeId ?? '',
    },
  });

  return (
    <CardWrapper
      title="Notification settings for the beneficiary"
      description="Information sharing, and the notifications sent to the beneficiary escrow members."
      secondaryDescription="No other notifications will automatically be shared with the beneficiary of a bipartite escrow."
    >
      <Card>
        <CardHeader>
          <CardTitle>Activation notification</CardTitle>
          <CardDescription>
            The notification sent to the beneficiary representatives when this escrow is activated.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {suggestedChange.payload?.['activation_notification'] !== undefined && (
            <SuggestedChangesView
              items={[
                getTechnicalSuggestedChangesItems(
                  'activationNotification',
                  suggestedChange.payload?.['activation_notification'],
                ),
              ]}
              className="mb-6"
            />
          )}
          <CardInfo
            title={activationNotificationOptionsRichDictionary[activationNotification.toString()].label}
            description={activationNotificationOptionsRichDictionary[activationNotification.toString()].description}
            icon={activationNotificationOptionsRichDictionary[activationNotification.toString()].icon}
          />
        </CardContent>
      </Card>
    </CardWrapper>
  );
};

const RequestChangesActions = ({
  suggestedChange,
}: {
  suggestedChange: ISuggestedChangesQuery['suggestedChanges']['nodes'][0];
}) => {
  const {
    saasEscrow: {
      id,
      policy: { createNotificationSuggestedChange },
    },
  } = useSaasEscrow();
  const navigate = useNavigate();

  const onRequestChanges = () => navigate(`/saas-escrows/${id}/request-changes/technical`);

  const actions = useMemo(
    () => [
      {
        id: 'review',
        component: (
          <Button variant="outline" onClick={onRequestChanges}>
            <PencilLine />
            Request changes
          </Button>
        ),
        condition: createNotificationSuggestedChange,
      },
      {
        id: 'requestChanges',
        component: <RequestChangesTooltip />,
        condition: suggestedChange,
      },
    ],
    [createNotificationSuggestedChange, suggestedChange],
  );

  const actionsFiltered = actions.filter((action) => action.condition);

  if (!actionsFiltered.length) return null;

  return (
    <div className="flex justify-end gap-3 mb-8">
      {actionsFiltered.map(({ id, component }) => (
        <Fragment key={id}>{component}</Fragment>
      ))}
    </div>
  );
};

const TechnicalViewContainer = () => {
  const {
    saasEscrow: { id, assetVersionsLimit, storageRegion, agreement },
  } = useSaasEscrow();
  const {
    data: { suggestedChanges },
  } = useSuspenseQuery(SuggestedChangesDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      escrowId: id,
      changeType: SuggestedChangeChangeTypeEnum.NotificationSuggestedChange,
    },
  });

  return (
    <div className="space-y-8">
      <RequestChangesActions suggestedChange={suggestedChanges.nodes[0]} />

      <CardWrapper
        title="Notification settings for the depositor"
        description="Information sharing, and the notifications sent to the depositor escrow members."
      >
        <NotificationsSettings
          partyType={EscrowPartyTypeEnum.Depositor}
          suggestedChangeId={suggestedChanges.nodes[0]?.id}
        />
      </CardWrapper>

      <Separator />

      {agreement?.agreementForm === AgreementFormEnum.Bipartite ? (
        <BipartiteNotificationSettings suggestedChangeId={suggestedChanges.nodes[0]?.id} />
      ) : (
        <TripartiteNotificationSettings suggestedChangeId={suggestedChanges.nodes[0]?.id} />
      )}

      <Separator />

      <CardWrapper
        title="Storage settings"
        description="Configuration of the deposited assets and their storage in escrow."
      >
        <Card>
          <CardHeader>
            <CardTitle>Storage region</CardTitle>
            <CardDescription>
              The geographical region where the storage of assets connected to this escrow will be limited to.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {suggestedChanges.nodes[0]?.payload?.['storage_region'] && (
              <SuggestedChangesView
                items={[
                  getTechnicalSuggestedChangesItems(
                    'storageRegion',
                    suggestedChanges.nodes[0].payload['storage_region'],
                  ),
                ]}
                className="mb-6"
              />
            )}
            <CardInfo
              title={storageRegion ? readOnlyStorageRegionDictionary[storageRegion] : ''}
              description="Selecting a specific region requires the Advanced Deposits Add-On."
              icon={MapPin}
              badge={storageRegion !== 'default' && <Badge className="whitespace-nowrap">Add-On</Badge>}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Asset version history</CardTitle>
            <CardDescription>
              The minimum number of most recent versions of each asset that will be kept in escrow.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {suggestedChanges.nodes[0]?.payload?.['asset_versions_limit'] !== undefined && (
              <SuggestedChangesView
                items={[
                  getTechnicalSuggestedChangesItems(
                    'assetVersionsLimit',
                    suggestedChanges.nodes[0].payload['asset_versions_limit'],
                  ),
                ]}
                className="mb-6"
              />
            )}
            <CardInfo
              title={assetVersionsLimitOptionsDictionary[assetVersionsLimit ? assetVersionsLimit.toString() : 'full']}
              icon={History}
              description="Storing the full version history requires the Advanced Deposits Add-On."
              descriptionStyle="italic"
              badge={assetVersionsLimit === null && <Badge className="whitespace-nowrap">Add-On</Badge>}
            />
          </CardContent>
        </Card>
      </CardWrapper>
    </div>
  );
};

function getDisruptedNotificationTitle(disruptionNotification: DisruptionNotificationEnum, gracePeriod: string) {
  if (disruptionNotification === DisruptionNotificationEnum.SendNotification) {
    return disruptedNotificationsOptionsRichDictionary[disruptionNotification].label.concat(
      ` (${gracePeriodOptionsDictionary[gracePeriod].label})`,
    );
  }

  return disruptedNotificationsOptionsRichDictionary[disruptionNotification].label;
}

export default TechnicalViewContainer;
